import type { AxiosRequestConfig } from 'axios'
import { BASE_API_URL } from '@/utils/constants'
import type { IApiRequest } from '@/types/api-types'
import AxiosServiceProvider from '@/services/AxiosServiceProvider'
import AuthService from '@/services/AuthService'
import CookieStorageService from '@/services/CookieStorageService'
import type { Filters } from '@/types/service-types/FilterExpressionGenerator.d'
import FilterExpressionGenerator from '@/services/FilterExpressionGenerator'

class BaseApiRequest implements IApiRequest {
  private requestProvider: AxiosServiceProvider

  protected authProvider: AuthService

  constructor(unabortable?: boolean, handlingExplicitly?: boolean) {
    this.requestProvider = new AxiosServiceProvider(
      BaseApiRequest.getConfig(),
      unabortable,
      handlingExplicitly
    )
    this.authProvider = new AuthService(this.requestProvider)
  }

  private static getConfig(): AxiosRequestConfig {
    const cookieStorageService = new CookieStorageService()
    const token = cookieStorageService.getData('accessToken')
    if (!token?.length) {
      return {
        baseURL: BASE_API_URL
      }
    }
    return {
      baseURL: BASE_API_URL,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  }

  private static appendQueryParamsToPath(path: string, params: Filters) {
    const filterGenerator = new FilterExpressionGenerator(params)
    const query = filterGenerator.generateFilterExpression()

    return `${path}?${query}`
  }

  get(apiUrl: string, queryParams?: any, generatedFilterExpression?: string) {
    let path = apiUrl
    if (queryParams) {
      path = BaseApiRequest.appendQueryParamsToPath(apiUrl, queryParams)
    }
    if (generatedFilterExpression) {
      path += `${queryParams ? '&' : '?'}${generatedFilterExpression}`
    }
    return this.requestProvider.sendGetRequest(path)
  }

  post(apiUrl: string, data: object, queryParams?: any) {
    let path = apiUrl
    if (queryParams) {
      path = BaseApiRequest.appendQueryParamsToPath(apiUrl, queryParams)
    }
    return this.requestProvider.sendPostRequest(path, data)
  }

  put(apiUrl: string, data: object, queryParams?: any) {
    let path = apiUrl
    if (queryParams) {
      path = BaseApiRequest.appendQueryParamsToPath(apiUrl, queryParams)
    }
    return this.requestProvider.sendPutRequest(path, data)
  }

  delete(apiUrl: string, queryParams?: any) {
    let path = apiUrl
    if (queryParams) {
      path = BaseApiRequest.appendQueryParamsToPath(apiUrl, queryParams)
    }

    return this.requestProvider.sendDeleteRequest(path)
  }
}

export default BaseApiRequest
